import { Page, IndexTable, Layout, Box, Badge, useBreakpoints, Text, Link, Spinner, Button, Card } from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useFindMany } from "@gadgetinc/react";
import { api } from "../services/api";
import { duplicates } from "../assets";
import { duplicateBanner } from "../assets";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { makeShop, ShopQuery } from "../components/shop/queries/shopQuery";

interface OrderInfo {
  wasBlocked: boolean;
  orderId?: string;
  customerId?: string;
  customerEmail?: string;
  matchType?: string;
  action?: string;
}

interface BlockedDiscount {
  id: string;
  discountCode?: string | null;
  checkoutEmail?: string | null;
  checkoutPhone?: string | null;
  checkoutToken?: string | null;
  orderList?: OrderInfo[];
  createdAt: Date;
}

interface OrderData {
  orderId?: string;
  totalPrice?: string;
}

interface CheckoutStatus {
  error?: boolean;
  message?: string;
  orderCreated?: boolean;
  orderData?: OrderData;
}

interface ProcessedGroup {
  groupId: string;
  groupName: string;
  items: BlockedDiscount[];
  earliestItemDate: Date;
}

function getBlockedDiscountLinkers(item: BlockedDiscount): Set<string> {
  const linkers = new Set<string>();
  if (item.orderList && Array.isArray(item.orderList)) {
    item.orderList.forEach(orderInfo => {
      if (orderInfo.customerId) linkers.add(`cid:${orderInfo.customerId}`);
      if (orderInfo.customerEmail) linkers.add(`email:${orderInfo.customerEmail}`);
    });
  }
  return linkers;
}

function determineGroupName(item: BlockedDiscount): string {
  if (item.orderList && item.orderList.length > 0) {
    const firstOrderWithEmail = item.orderList.find(oi => oi.customerEmail);
    if (firstOrderWithEmail?.customerEmail) return firstOrderWithEmail.customerEmail;
    const firstOrderWithCustomerId = item.orderList.find(oi => oi.customerId);
    if (firstOrderWithCustomerId?.customerId) return `Customer ${firstOrderWithCustomerId.customerId}`;
  }
  if (item.checkoutEmail) return item.checkoutEmail;
  return `Group ID ${item.id.substring(0, 8)}`;
}

function consolidateBlockedDiscounts(discounts: BlockedDiscount[]): ProcessedGroup[] {
  if (!discounts || discounts.length === 0) return [];
  const sortedDiscounts = [...discounts].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  let unprocessedItems = [...sortedDiscounts];
  const finalGroups: Array<{
    items: BlockedDiscount[];
    linkers: Set<string>;
    earliestItem: BlockedDiscount;
  }> = [];

  while (unprocessedItems.length > 0) {
    const currentItem = unprocessedItems.shift();
    if (!currentItem) continue;
    const currentGroup = { items: [currentItem], linkers: getBlockedDiscountLinkers(currentItem), earliestItem: currentItem };
    let i = 0;
    while (i < unprocessedItems.length) {
      const otherItem = unprocessedItems[i];
      if (!otherItem) { i++; continue; }
      const otherLinkers = getBlockedDiscountLinkers(otherItem);
      const intersection = [...currentGroup.linkers].filter(linker => otherLinkers.has(linker));
      if (intersection.length > 0) {
        currentGroup.items.push(otherItem);
        otherLinkers.forEach(linker => currentGroup.linkers.add(linker));
        unprocessedItems.splice(i, 1);
      } else {
        i++;
      }
    }
    finalGroups.push(currentGroup);
  }

  return finalGroups.map((group, index) => {
    group.items.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    const groupName = determineGroupName(group.earliestItem);
    return {
      groupId: `${groupName}-${group.earliestItem.id}-${index}`,
      groupName: groupName,
      items: group.items,
      earliestItemDate: new Date(group.earliestItem.createdAt),
    };
  }).sort((a, b) => a.earliestItemDate.getTime() - b.earliestItemDate.getTime());
}

export const Duplicates = () => {
  const navigate = useNavigate();
  const { data: shopData } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;
  const [{ data, error, fetching }] = useFindMany(api.duplicativeCustomerDiscountBlocked, {
    filter: shop?.id ? { shopId: { equals: shop.id } } : undefined,
    pause: !shop?.id // Don't run the query until we have a shop ID
  });
  
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [processedGroups, setProcessedGroups] = useState<ProcessedGroup[]>([]);
  const [checkoutStatuses, setCheckoutStatuses] = useState<Record<string, CheckoutStatus>>({});
  const [loadingStatuses, setLoadingStatuses] = useState<Record<string, boolean>>({});

  const isRowExpanded = (id: string) => expandedRows[id] || false;
  const toggleRowExpanded = (id: string) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  
  const rawApiData = data || []; 

  const resourceName = { singular: "Blocked Usage", plural: "Blocked Usages" };
  const hasBlocking = shop && shop.subscribedTo("blocking");

  const generateShopifyUrl = (type: string, id: string): string => {
    if (!shop?.domain) return "#";
    return `https://${shop.domain}/admin/${type === 'customer' ? 'customers' : 'orders'}/${id}`;
  };

  useEffect(() => {
    if (rawApiData && rawApiData.length > 0) {
      const mappedDiscounts: BlockedDiscount[] = rawApiData.map((record: any) => {
        let parsedOrderList: OrderInfo[] = [];
        if (typeof record.orderList === 'string') {
          try { const parsed = JSON.parse(record.orderList); if (Array.isArray(parsed)) parsedOrderList = parsed; } 
          catch (e) { console.error("Failed to parse orderList:", record.id, e); }
        } else if (Array.isArray(record.orderList)) {
          parsedOrderList = record.orderList;
        }
        return {
          id: record.id,
          createdAt: new Date(record.createdAt),
          discountCode: record.discountCode ?? null,
          checkoutEmail: record.checkoutEmail ?? null,
          checkoutPhone: record.checkoutPhone ?? null,
          checkoutToken: record.checkoutToken ?? null,
          orderList: parsedOrderList.map(o => ({...o})),
        };
      });
      setProcessedGroups(consolidateBlockedDiscounts(mappedDiscounts));
    } else {
      setProcessedGroups([]);
    }
  }, [rawApiData]);

  useEffect(() => {
    if (!shop?.id || !processedGroups.length) return;

    processedGroups.forEach(group => {
      group.items.forEach(async item => {
        if (item.checkoutToken && !checkoutStatuses[item.id] && !loadingStatuses[item.id]) {
          setLoadingStatuses(prev => ({ ...prev, [item.id]: true }));
          try {
            const response = await axios.get(`/api/checkout-status?token=${encodeURIComponent(item.checkoutToken)}&shopId=${encodeURIComponent(shop.id)}`);
            setCheckoutStatuses(prev => ({ ...prev, [item.id]: response.data }));
          } catch (err) {
            console.error(`Error checking status for ${item.checkoutToken}:`, err);
            setCheckoutStatuses(prev => ({ ...prev, [item.id]: { error: true, message: err instanceof Error ? err.message : 'Unknown error' } }));
          } finally {
            setLoadingStatuses(prev => ({ ...prev, [item.id]: false }));
          }
        }
      });
    });
  }, [processedGroups, shop?.id, checkoutStatuses, loadingStatuses]); // Added dependencies to control re-runs

  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `.Polaris-IndexTable__TableCell { vertical-align: top !important; }`;
    document.head.appendChild(styleTag);
    return () => { document.head.removeChild(styleTag); };
  }, []);

  const wasBlocked = (item: BlockedDiscount): boolean => item.orderList?.some(order => order.wasBlocked === true || (order.matchType && order.wasBlocked === false)) || false;

  const renderCheckoutStatus = (item: BlockedDiscount) => {
    if (!wasBlocked(item)) return null;
    if (loadingStatuses[item.id]) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Spinner size="small" /><div style={{ marginLeft: '8px' }}><Text as="span" variant="bodyMd">Checking...</Text></div>
        </div>
      );
    }
    const status = checkoutStatuses[item.id];
    if (!status) return null;
    if (status.error) return <Text as="span" variant="bodyMd" tone="critical">Error checking status</Text>;
    if (status.orderCreated && status.orderData) {
      const { orderId = '', totalPrice = '0.00' } = status.orderData;
      return (
        <div>
          <Badge tone="warning">Order Completed</Badge>
          <div style={{ marginTop: '4px' }}>
            <Text as="p" variant="bodyMd">Order: <Link url={generateShopifyUrl('order', orderId)} external>#{orderId}</Link></Text>
            <Text as="p" variant="bodySm" tone="subdued">Total: ${totalPrice}</Text>
          </div>
        </div>
      );
    }
    return <Badge>Checkout Abandoned</Badge>;
  };

  const getMatchType = (orderList: OrderInfo[] | undefined): string | null => {
    if (!orderList || orderList.length === 0 || orderList[0]?.action === "allowed") return null;
    const matchingOrder = orderList.find(order => order.matchType);
    if (matchingOrder?.matchType) return matchingOrder.matchType.charAt(0).toUpperCase() + matchingOrder.matchType.slice(1);
    return "Unknown";
  };

  const formatOrderList = (orderList: OrderInfo[] | undefined) => {
    if (!orderList || orderList.length === 0) return "No matching orders";
    if (orderList[0]?.action === "allowed") return <Text as="p" variant="bodyMd" fontWeight="bold">Valid first-time usage</Text>;
    return orderList.map((order, index) => (
      <div key={index} style={{ marginBottom: index < orderList.length - 1 ? '16px' : '0', padding: '4px 0' }}>
        <div style={{ marginTop: '4px' }}>
          <Text as="p" variant="bodyMd">
            <Text as="span" tone="subdued">Order: </Text>
            {order.orderId ? <Link url={generateShopifyUrl('order', order.orderId)} external>#{order.orderId}</Link> : "N/A"}
          </Text>
        </div>
        <div style={{ marginTop: '4px' }}>
          <Text as="p" variant="bodyMd">
            <Text as="span" tone="subdued">Customer: </Text>
            {order.customerId ? <Link url={generateShopifyUrl('customer', order.customerId)} external>#{order.customerId}</Link> : "N/A"}
          </Text>
        </div>
      </div>
    ));
  };

  if (!hasBlocking) {
    return (
      <div style={{ maxWidth: "80rem", margin: "auto" }}>
        <Page backAction={{ content: "Dashboard", onAction: () => navigate("/") }} title="Duplicate Blocking" subtitle="You need to upgrade to get access to duplicate blocking.">
          <img width="100%" style={{ borderRadius: "0.75rem" }} src={duplicates} alt="Upgrade Required" />
        </Page>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "80rem", margin: "auto" }}>
      <Page title="Duplicate Blocking" subtitle="Stay on top of customers creating multiple accounts to take advantage of your referral program, new customer discounts, and more." backAction={{ content: "Dashboard", onAction: () => navigate("/") }}>
        <Layout>
          <Layout.Section variant="fullWidth"><Box><img src={duplicateBanner} style={{ width: "100%", borderRadius: "0.5rem" }} alt="Duplicate Blocking Banner" /></Box></Layout.Section>
          <Layout.Section variant="fullWidth">
            {fetching && <Box padding="400"><div style={{ display: "flex", justifyContent: "center" }}><Spinner /></div></Box>}
            {error && <Box padding="400"><Text as="p" tone="critical">Error loading data: {error.message}</Text></Box>}
            <Card padding="0">
              <IndexTable condensed={useBreakpoints().smDown} resourceName={resourceName} itemCount={rawApiData.length} selectable={false} headings={[{ title: <span>Customer</span>, id: 'case-header' }, { title: <span>Coupon Code</span>, id: 'coupon-code-header' }, { title: "Block Reason" }, { title: "Checkout Status" }, { title: "Date" }]}>
                {fetching && <IndexTable.Row id="loading-row" key="loading-row" position={0}><IndexTable.Cell colSpan={5}><div style={{ display: "flex", justifyContent: "center", padding: "20px" }}><Spinner /></div></IndexTable.Cell></IndexTable.Row>}
                {error && !fetching && <IndexTable.Row id="error-row" key="error-row" position={0}><IndexTable.Cell colSpan={5}><div style={{ padding: "20px", textAlign: "center" }}><Text as="p" tone="critical">Error loading data: {error.message}</Text></div></IndexTable.Cell></IndexTable.Row>}
                {!fetching && !error && (
                  <>
                    {processedGroups.length > 0 ? (
                      <>
                        {(() => {
                          let globalRowIndex = 0;
                          return processedGroups.flatMap(group => [
                            <IndexTable.Row key={group.groupId} id={group.groupId} accessibilityLabel={`Group ${group.groupName}`} position={globalRowIndex++}>
                              <IndexTable.Cell><Text as="span" fontWeight="semibold" variant="bodyMd">{group.groupName}</Text></IndexTable.Cell>
                              <IndexTable.Cell /><IndexTable.Cell /><IndexTable.Cell /><IndexTable.Cell />
                            </IndexTable.Row>,
                            ...group.items.map(item => (
                              <IndexTable.Row rowType="child" id={String(item.id)} key={String(item.id)} position={globalRowIndex++}>
                                <IndexTable.Cell><Text as="span" variant="bodyMd">{item.checkoutEmail || "No email"}</Text></IndexTable.Cell>
                                <IndexTable.Cell><Text as="span" variant="bodyMd" fontWeight="bold">{item.discountCode?.toUpperCase() || "N/A"}</Text></IndexTable.Cell>
                                <IndexTable.Cell>
                                  <div style={{ position: 'relative' }}>
                                    {wasBlocked(item) ? (
                                      <>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                          <div><Badge tone="critical">{`${getMatchType(item.orderList) ?? 'Unknown'} Match`}</Badge></div>
                                          <button onClick={() => toggleRowExpanded(item.id)} aria-label="Toggle details" style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5c5f62' }}>
                                            {isRowExpanded(item.id) ? <svg width="16" height="16" viewBox="0 0 20 20"><path d="M5 12.5L10 7.5L15 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> : <svg width="16" height="16" viewBox="0 0 20 20"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                          </button>
                                        </div>
                                        {isRowExpanded(item.id) && (
                                          <div style={{ marginTop: '12px', marginBottom: '12px', padding: '8px', background: 'rgba(0,0,0,0.03)', borderRadius: '4px', border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', maxWidth: '300px' }}>
                                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flexDirection: 'column' }}>
                                              <div style={{ flex: '1' }}> {/* Checkout Details */}
                                                <Text as="p" variant="bodySm" fontWeight="medium">Checkout Details:</Text>
                                                <div style={{ marginTop: '4px' }}>
                                                  <div style={{ marginTop: '4px' }}><Text as="p" variant="bodyMd"><Text as="span" tone="subdued">Phone: </Text>{item.checkoutPhone || "N/A"}</Text></div>
                                                  {/* item.checkoutAddress related rendering removed as it's not on BlockedDiscount type */}
                                                  <div style={{ marginTop: '4px' }}><Text as="p" variant="bodyMd"><Text as="span" tone="subdued">Code: </Text>{item.discountCode?.toUpperCase() || "N/A"}</Text></div>
                                                </div>
                                              </div>
                                              <div style={{ flex: '1' }}> {/* Matching Details */}
                                                <Text as="p" variant="bodySm" fontWeight="medium">Previous Utilization by Matched Customer:</Text>
                                                <div style={{ marginTop: '4px' }}>
                                                  {formatOrderList(item.orderList)}
                                                  <div style={{ marginTop: '4px' }}><Text as="p" variant="bodyMd"><Text as="span" tone="subdued">Code: </Text>{item.discountCode?.toUpperCase() || "N/A"}</Text></div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    ) : <div><Badge tone="success">Valid Usage</Badge></div>}
                                  </div>
                                </IndexTable.Cell>
                                <IndexTable.Cell>{renderCheckoutStatus(item)}</IndexTable.Cell>
                                <IndexTable.Cell><Text as="span" variant="bodyMd">{new Date(item.createdAt).toLocaleDateString()}</Text></IndexTable.Cell>
                              </IndexTable.Row>
                            ))
                          ]);
                        })()}
                      </>
                    ) : <IndexTable.Row id="empty" key="empty-row" position={0}><IndexTable.Cell colSpan={5}><div style={{ padding: "20px", textAlign: "center" }}>No blocked discount usage records found</div></IndexTable.Cell></IndexTable.Row>}
                  </>
                )}
              </IndexTable>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    </div>
  );
}
